import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';

interface ChatInputProps {
  onSendMessage: (message: string, attachment?: File) => void;
  disabled?: boolean;
}

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled }) => {
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cleanup effect to remove PWA container wrappers injected by browser extensions
  useEffect(() => {
    const cleanupPWAWrappers = () => {
      if (containerRef.current) {
        const wrappers = containerRef.current.querySelectorAll('pwa-container-wrapper');
        wrappers.forEach(wrapper => wrapper.remove());
      }
    };

    // Run cleanup immediately
    cleanupPWAWrappers();

    // Set up interval to periodically clean up (every 500ms)
    const intervalId = setInterval(cleanupPWAWrappers, 500);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() || attachment) {
      onSendMessage(message.trim(), attachment || undefined);
      setMessage('');
      setAttachment(null);
      setFileError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size must not exceed 10MB. Current size: ${(file.size / 1024 / 1024).toFixed(1)}MB`;
    }

    // Check file type
    const fileName = file.name.toLowerCase();
    const isValidType = ALLOWED_FILE_TYPES.some(type => fileName.endsWith(type));
    if (!isValidType) {
      return `File type not supported. Please upload PDF, DOC, or DOCX files only.`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError('');
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const error = validateFile(file);
      
      if (error) {
        setFileError(error);
        setAttachment(null);
        // Clear the input
        e.target.value = '';
      } else {
        setAttachment(file);
      }
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setFileError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border-t border-jobesta-gray-40 bg-white p-4">
      {fileError && (
        <div className="mb-2 p-2 bg-jobesta-red bg-opacity-10 border border-jobesta-red rounded-jobesta text-jobesta-red text-sm">
          {fileError}
        </div>
      )}
      {attachment && (
        <div className="mb-2">
          <div className="flex items-center bg-jobesta-gray-30 rounded-jobesta px-3 py-2 text-sm">
            <svg className="w-4 h-4 mr-2 text-jobesta-gray-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="truncate max-w-xs flex-1 text-jobesta-gray-90">{attachment.name}</span>
            <span className="text-jobesta-gray-60 text-xs ml-2">({(attachment.size / 1024 / 1024).toFixed(1)}MB)</span>
            <button
              type="button"
              onClick={removeAttachment}
              className="ml-2 text-jobesta-red hover:text-jobesta-red hover:opacity-80 p-1"
              title="Remove file"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      <div ref={containerRef} className="chat-input-container flex items-end space-x-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="p-2 text-jobesta-gray-60 hover:text-jobesta-blue focus:outline-none transition-colors"
          disabled={disabled || !!attachment}
          title={attachment ? 'Remove current file to upload a new one' : 'Upload file (PDF, DOC, DOCX)'}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
          className="hidden"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          disabled={disabled}
          rows={1}
          className="chat-input flex-1 resize-none rounded-jobesta border border-jobesta-gray-40 px-4 py-2 text-jobesta-gray-90 placeholder-jobesta-gray-60 focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue disabled:opacity-50"
          style={{ minHeight: '44px', maxHeight: '200px' }}
        />
        <button
          type="submit"
          disabled={disabled || (!message.trim() && !attachment) || !!fileError}
          className="p-2 bg-jobesta-blue text-white rounded-jobesta hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg
            className="w-6 h-6 rotate-90"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
    </form>
  );
};

export default ChatInput;